// 담당: 현정
// 수집 신선도 — 날짜 → 상태 판정(순수 함수). SNS 데이터 유효성 v1 §5.1~§5.2.
//
// 축 A(채널 지표: 팔로워·콘텐츠수·누적조회) 기준이다. 콘텐츠/브랜디드 지표(축 B)는 나이가 다르므로
// 이 모듈로 판정하지 않는다 — 배지 하나로 뭉치면 그 배지가 새 거짓말이 된다.
//
// BE는 날짜만 내리고 임계는 FE가 적용한다(임계를 BE가 구우면 정책 변경이 배포 2건이 된다).
//   최신 ≤ D+2 · 지연 D+3~ · 중단 의심 D+14~ · 대상 없음(날짜 자체가 없음)
// 계산 기준 시각은 브라우저 로컬로 통일하고, 미래 날짜는 D+0으로 클램프한다
// (음수 경과일이 표기를 통째로 지워버리는 것을 막는다).
export type FreshnessState = 'fresh' | 'delayed' | 'stalled' | 'none'

export const DELAY_DAYS = 3
export const STALL_DAYS = 14

/** 색은 도트에만 쓴다 — 11px 텍스트에 경고색을 쓰면 대비 AA에 미달한다. */
export const FRESHNESS_DOT: Record<FreshnessState, string> = {
  fresh: 'bg-status-success',
  delayed: 'bg-status-warning',
  stalled: 'bg-status-danger',
  none: 'bg-fg-3',
}

export const FRESHNESS_LABEL: Record<FreshnessState, string> = {
  fresh: '최신',
  delayed: '지연',
  stalled: '중단 의심',
  none: '대상 없음',
}

/** "YYYY-MM-DD" → 로컬 자정 Date. 형식이 다르면 null(추측 파싱 안 한다). */
function parseLocalDate(v: string | null | undefined): Date | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** 경과일. 값 없거나 파싱 불가면 null. 미래 날짜는 0으로 클램프. */
export function daysSince(v: string | null | undefined, now: Date = new Date()): number | null {
  const d = parseLocalDate(v)
  if (!d) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diff = Math.floor((today.getTime() - d.getTime()) / 86_400_000)
  return diff < 0 ? 0 : diff
}

export function freshnessState(v: string | null | undefined, now: Date = new Date()): FreshnessState {
  const days = daysSince(v, now)
  if (days == null) return 'none'
  if (days >= STALL_DAYS) return 'stalled'
  if (days >= DELAY_DAYS) return 'delayed'
  return 'fresh'
}

/** 좁은 컬럼용 날짜 — 올해면 "08.10", 다른 해면 "25.12.20". 파싱 불가면 null. */
export function formatMonthDay(v: string | null | undefined, now: Date = new Date()): string | null {
  const d = parseLocalDate(v)
  if (!d) return null
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  if (d.getFullYear() === now.getFullYear()) return `${mm}.${dd}`
  return `${String(d.getFullYear()).slice(2)}.${mm}.${dd}`
}

/** 가장 오래된 날짜(보수적 기준값). 유효한 날짜가 없으면 null. */
export function oldestDate(values: (string | null | undefined)[]): string | null {
  let oldest: string | null = null
  for (const v of values) {
    if (!parseLocalDate(v) || !v) continue
    if (oldest == null || v < oldest) oldest = v // YYYY-MM-DD는 사전순 = 시간순
  }
  return oldest
}
